import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { X, Send, Paperclip, FileDown, Loader2, MessageSquare } from "lucide-react";
import { generateReceipt } from "../lib/receipt";
import { useLanguage } from "../lib/language";

/**
 * Full chat UI for a support ticket — used from the customer's Profile so they
 * can open a ticket, read past messages, reply, upload the deposit proof, and
 * download the receipt of the linked order.
 *
 * Props:
 *   open · boolean
 *   ticket · full ticket object ({_id, subject, status, order_id, messages[]})
 *   onClose · ()=>void
 *   onUpdated · (updatedTicket)=>void  // called after a reply
 *   settings · app settings (for receipt branding)
 */
export default function TicketChatModal({ open, ticket, onClose, onUpdated, settings }) {
    const [message, setMessage] = useState("");
    const [attachments, setAttachments] = useState([]); // [{name, data (base64), mime}]
    const [sending, setSending] = useState(false);
    const [order, setOrder] = useState(null);
    const [loadingOrder, setLoadingOrder] = useState(false);
    const scrollRef = useRef(null);
    const fileInputRef = useRef(null);
    const { t, lang } = useLanguage();

    // Lock body scroll
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    // Load linked order (for receipt + tracking)
    useEffect(() => {
        if (!open || !ticket?.order_id) { setOrder(null); return; }
        setLoadingOrder(true);
        api.get(`/orders/${ticket.order_id}`)
            .then(({ data }) => setOrder(data))
            .catch(() => setOrder(null))
            .finally(() => setLoadingOrder(false));
    }, [open, ticket?.order_id]);

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [ticket?.messages?.length, open]);

    // Live polling: while the modal is open, refresh the ticket every 5s so new
    // admin/customer messages appear automatically without a manual refresh.
    // Uses ETag-like short-circuit: skips state update if message count is unchanged.
    useEffect(() => {
        if (!open || !ticket?._id) return;
        let cancelled = false;
        const currentCount = ticket.messages?.length || 0;
        const tick = async () => {
            try {
                const { data } = await api.get(`/tickets/${ticket._id}`);
                if (cancelled) return;
                if ((data?.messages?.length || 0) !== currentCount || data?.status !== ticket.status) {
                    onUpdated?.(data);
                }
            } catch (_) { /* silent — will retry next interval */ }
        };
        const iv = setInterval(tick, 5000);
        // Also poll when tab becomes visible again (e.g., customer returns to the app)
        const onVis = () => { if (document.visibilityState === "visible") tick(); };
        document.addEventListener("visibilitychange", onVis);
        return () => {
            cancelled = true;
            clearInterval(iv);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, [open, ticket?._id, ticket?.messages?.length, ticket?.status, onUpdated]);

    const pickFiles = () => fileInputRef.current?.click();
    const onFiles = (e) => {
        const files = Array.from(e.target.files || []);
        Promise.all(files.map((f) => new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => resolve({ name: f.name, data: r.result, mime: f.type });
            r.onerror = reject;
            r.readAsDataURL(f);
        }))).then((arr) => setAttachments((prev) => [...prev, ...arr]));
        e.target.value = "";
    };
    const removeAttachment = (idx) => setAttachments((prev) => prev.filter((_, i) => i !== idx));

    const send = async () => {
        if (!message.trim() && attachments.length === 0) {
            toast.error(t("Escribe un mensaje o adjunta el comprobante", "Write a message or attach the receipt"));
            return;
        }
        setSending(true);
        try {
            const { data } = await api.post(`/tickets/${ticket._id}/reply`, {
                message: message.trim() || (lang === "en" ? "(attachment)" : "(adjunto)"),
                attachments: attachments,
            });
            setMessage(""); setAttachments([]);
            onUpdated?.(data);
            toast.success(t("Mensaje enviado", "Message sent"));
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("No se pudo enviar el mensaje", "Could not send the message"));
        } finally {
            setSending(false);
        }
    };

    const downloadReceipt = () => {
        if (!order) { toast.error(t("Recibo no disponible", "Receipt not available")); return; }
        try {
            generateReceipt(order, settings || {});
        } catch (e) {
            toast.error(t("No se pudo generar el recibo", "Could not generate the receipt"));
        }
    };

    if (!open || !ticket) return null;
    const msgs = ticket.messages || [];

    return createPortal(
        <div className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4" data-testid="ticket-chat-modal">
            <div className="bg-[var(--tb-bg)] border border-[var(--tb-gold)]/50 rounded-t-lg sm:rounded-lg w-full max-w-2xl shadow-2xl flex flex-col max-h-[92vh]">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-[var(--tb-border)]">
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-[10px] text-[var(--tb-muted)]">
                            <MessageSquare className="w-3 h-3" />
                            <span className="font-mono">{ticket._id}</span>
                            <span className={`px-1.5 rounded ${ticket.status === "closed" ? "bg-slate-500/30 text-slate-300" : "bg-emerald-500/20 text-emerald-300"}`}>
                                {ticket.status}
                            </span>
                        </div>
                        <h3 className="font-medium text-sm truncate">{ticket.subject}</h3>
                    </div>
                    <button onClick={onClose} className="tb-btn-ghost !p-2" aria-label={t("Cerrar", "Close")} data-testid="ticket-chat-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Linked order summary + receipt */}
                {ticket.order_id && (
                    <div className="px-4 py-2 bg-[var(--tb-bg-2)]/40 border-b border-[var(--tb-border)] flex items-center gap-3 flex-wrap">
                        <div className="text-[10px] text-[var(--tb-muted)] min-w-0 flex-1">
                            <div>{t("Pedido", "Order")}: <span className="font-mono text-[var(--tb-text-2)]">{ticket.order_id.slice(0, 12)}</span></div>
                            {order?.c807_guia && (
                                <div className="text-emerald-300 mt-0.5">{t("Guía", "Waybill")}: <span className="font-mono">{order.c807_guia}</span></div>
                            )}
                        </div>
                        <button onClick={downloadReceipt} disabled={loadingOrder || !order}
                            className="tb-btn-secondary !text-[11px] !py-1 inline-flex items-center gap-1 disabled:opacity-50"
                            data-testid="ticket-chat-download-receipt">
                            <FileDown className="w-3.5 h-3.5" />
                            {loadingOrder ? "..." : t("Descargar recibo", "Download receipt")}
                        </button>
                    </div>
                )}

                {/* Messages */}
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[200px] max-h-[50vh]">
                    {msgs.length === 0 ? (
                        <div className="text-center text-[var(--tb-muted)] text-sm py-8">{t("Aún no hay mensajes.", "No messages yet.")}</div>
                    ) : msgs.map((m, i) => {
                        const isAdmin = m.from === "admin";
                        return (
                            <div key={i} className={`flex ${isAdmin ? "justify-start" : "justify-end"}`}>
                                <div className={`max-w-[85%] rounded-lg p-3 ${
                                    isAdmin
                                        ? "bg-[var(--tb-bg-2)] border border-[var(--tb-border)]"
                                        : "bg-[var(--tb-gold)]/15 border border-[var(--tb-gold)]/30"
                                }`}>
                                    <div className={`text-[10px] mb-1 ${isAdmin ? "text-[var(--tb-gold)]" : "text-emerald-300"}`}>
                                        {isAdmin ? t("Terra Boutique · Soporte", "Terra Boutique · Support") : t("Tú", "You")}
                                        {m.at && <span className="text-[var(--tb-muted)] ml-2">{new Date(m.at).toLocaleString(lang === "en" ? "en-US" : "es-SV", { dateStyle: "short", timeStyle: "short" })}</span>}
                                    </div>
                                    {m.text && <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>}
                                    {(m.attachments || []).length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            {m.attachments.map((a, j) => (
                                                <a key={j} href={a.data || a.url} target="_blank" rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-[var(--tb-bg)] border border-[var(--tb-border)] hover:border-[var(--tb-gold)]/60">
                                                    <Paperclip className="w-3 h-3" />
                                                    {a.name || `${t("archivo", "file")}-${j + 1}`}
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Attachments preview */}
                {attachments.length > 0 && (
                    <div className="px-4 py-2 border-t border-[var(--tb-border)] flex flex-wrap gap-2">
                        {attachments.map((a, i) => (
                            <div key={i} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-[var(--tb-bg-2)] border border-[var(--tb-border)]">
                                <Paperclip className="w-3 h-3 text-[var(--tb-gold)]" />
                                <span className="max-w-[140px] truncate">{a.name}</span>
                                <button onClick={() => removeAttachment(i)} className="text-rose-400 hover:text-rose-300 ml-1" aria-label={t("Quitar", "Remove")}>×</button>
                            </div>
                        ))}
                    </div>
                )}

                {/* Reply composer */}
                {ticket.status !== "closed" ? (
                    <div className="p-3 border-t border-[var(--tb-border)]">
                        <div className="flex items-end gap-2">
                            <textarea
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); }
                                }}
                                placeholder={ticket.tag === "transfer_payment"
                                    ? t("Escribe tu mensaje o adjunta el comprobante del depósito...", "Write your message or attach the deposit receipt...")
                                    : t("Escribe tu mensaje...", "Write your message...")}
                                rows={2}
                                className="tb-input flex-1 !py-2 resize-none text-sm"
                                data-testid="ticket-chat-input"
                            />
                            <input ref={fileInputRef} type="file" multiple accept="image/*,application/pdf" hidden onChange={onFiles} />
                            <button onClick={pickFiles} className="tb-btn-ghost !p-2" title={t("Adjuntar comprobante o imagen", "Attach receipt or image")} data-testid="ticket-chat-attach">
                                <Paperclip className="w-5 h-5" />
                            </button>
                            <button onClick={send} disabled={sending}
                                className="tb-btn-primary !p-2 disabled:opacity-50"
                                title={t("Enviar (Ctrl/⌘+Enter)", "Send (Ctrl/⌘+Enter)")}
                                data-testid="ticket-chat-send">
                                {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                            </button>
                        </div>
                        <div className="text-[10px] text-[var(--tb-muted)] mt-1">{t("Ctrl+Enter para enviar", "Ctrl+Enter to send")}</div>
                    </div>
                ) : (
                    <div className="p-4 border-t border-[var(--tb-border)] text-center text-xs text-[var(--tb-muted)]">
                        {t("Este ticket está cerrado. Abre uno nuevo si necesitas ayuda adicional.", "This ticket is closed. Open a new one if you need more help.")}
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}
